import React from 'react';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { getFlows, getHeadcount, getNotesPreview, getWithdrawScheduled, listAllStudents } from '../academyApi';
import { formatRate } from '../academyModel';
import { todayKST } from '../periodDates';
import { Box, ErrorNotice, NameLink, Spinner, useAcademyVersion, useAsync } from '../ui';
import SchoolGradeTable from './SchoolGradeTable';
import { ColumnBars } from './charts';
import { confirmedFlowRow, flowWithdrawalStats, groupFlowStats, groupStudentCounts, withdrawalPeriodSummary } from './reportModel';
import { WithdrawalRateChart } from './RateCharts';
import FloatingScrollArea from '../../common/FloatingScrollArea';

export const displayNumber = (value) => value == null ? '—' : Number(value).toLocaleString('ko-KR');

/** 기본은 현재 조건의 결과만 표시한다. 요약 차트는 조회 중 이전 결과와 그 기간을 함께 유지할 수 있다. */
export function useReport(loader, key, { keepPreviousData = false } = {}) {
  const version = useAcademyVersion();
  const currentKey = `${key}-${version}`;
  const result = useAsync(async (signal) => ({ key: currentKey, value: await loader(signal) }), currentKey);
  const current = result.data?.key === currentKey && !result.loading;
  const refreshing = keepPreviousData && !!result.data && !current && !result.error;
  return { ...result, refreshing, data: current || refreshing ? result.data.value : null };
}

function Result({ result, children, compact = false }) {
  if (result.error) return <ErrorNotice error={result.error} onRetry={result.reload} compact />;
  if (!result.data) return <Spinner label="통계 불러오는 중" compact={compact} />;
  return children(result.data);
}

export function ReportTable({ label, headings, children, compact = false }) {
  return (
    <FloatingScrollArea role="region" aria-label={label} axis="x" className="rounded-xl border border-gray-100">
      <table className={`w-full whitespace-nowrap text-xs ${compact ? '[&_td]:py-1 [&_th]:py-1' : ''}`}>
        <thead className="bg-gray-50"><tr>{headings.map((heading, i) => <th key={heading} scope="col" className={`p-2 font-medium text-gray-500 ${i ? 'text-right' : 'text-left'}`}>{heading}</th>)}</tr></thead>
        <tbody className="divide-y divide-gray-100">{children}</tbody>
      </table>
    </FloatingScrollArea>
  );
}

function GroupFlowTable({ data, groupBy, preview, today }) {
  const rows = data.buckets.flatMap((period) => (period.groups || []).map((row) => ({ ...row, period })));
  const shown = preview ? rows.slice(0, 3) : rows;
  if (!rows.length) return <p className="py-3 text-xs text-gray-500">조회 기간에 해당하는 {groupBy === 'teacher' ? '강사' : '반'}가 없습니다.</p>;
  return <div className={preview ? 'space-y-1.5' : 'space-y-3'}>
    {!preview && data.data_reliable_from && <p className="text-xs text-gray-500">집계 자료 시작일 {data.data_reliable_from}</p>}
    {data.buckets.some((period) => period.total?.estimated_events > 0) && <p className="text-xs text-gray-500">일부 날짜는 이관 자료를 기준으로 합니다.</p>}
    <ReportTable compact={preview} label="반·강사 수강 통계" headings={[preview ? (groupBy === 'teacher' ? '강사' : '반') : '기간 · 대상', '수강', '입반', '퇴반', '퇴반율']}>
      {shown.map((row) => {
        const { current, joined, left, rate } = groupFlowStats(row, groupBy, row.period, data.currentGroups, today);
        return <tr key={`${row.period.start}-${row.key}`}>
          <th scope="row" className="p-2 text-left font-normal text-gray-900"><span className="block font-medium">{row.label || '미지정'}</span>
            {!preview && <span className="text-[11px] text-gray-500">{row.period.start} ~ {row.period.end}</span>}
            {!preview && row.period.partial && <span className="ml-1 text-gray-500">일부 기간</span>}
          </th>
          {[current, joined, left].map((value, i) => <td key={i} className="p-2 text-right tabular-nums text-gray-700">{displayNumber(value)}</td>)}
          <td className="p-2 text-right tabular-nums text-gray-900">{formatRate(rate)}</td>
        </tr>;
      })}
    </ReportTable>
    {preview && rows.length > shown.length && <p className="text-[11px] text-gray-500">전체 {rows.length}개 중 {shown.length}개 표시</p>}
    {!preview && <div className="space-y-1 text-xs text-gray-500">
      <p>수강은 휴원·테스트 학생을 제외합니다. 지난 기간은 종료 뒤 남은 인원, 오늘을 포함한 기간은 오늘 수강 중인 인원입니다. 오늘이 마지막 수강일인 학생도 수강에 포함합니다.</p>
      <p>입반은 반 이동으로 들어온 경우를 포함합니다. 퇴반은 휴원·학원 퇴원·일반 퇴반으로 수강을 중단한 횟수(건)입니다. 휴원 뒤 복귀 없이 퇴원·퇴반하면 다시 세지 않습니다. 반 이동·정상 종강·담임 변경은 퇴반에서 제외합니다.</p>
      <p>퇴반율 = 퇴반 횟수 ÷ (기간 말 수강 인원 + 퇴반 횟수). 기간 말 수강은 해당 날짜가 끝난 뒤에도 계속 다니는 인원이며, 표의 오늘 수강 수와 다를 수 있습니다. 예정 인원은 입반·퇴반에 넣지 않으며 미래 기간은 —로 표시합니다. 같은 학생이 다시 입반·퇴반하면 각각 집계합니다.</p>
      {groupBy === 'teacher' && <p>수강은 같은 강사의 여러 반에 있는 학생을 한 명으로 셉니다. 같은 강사의 반끼리 이동한 경우는 입반·퇴반으로 세지 않습니다.</p>}
    </div>}
  </div>;
}

/** 휴원 포함 중단 횟수와 서버의 기간 말 수강 인원으로 비율을 계산한다. 여러 기간의 비율을 더하거나 평균 내지 않는다. */
export function FlowContent({ from, to, bucket = 'month', groupBy = 'none', preview = false, summaryAction }) {
  const { getAccessToken } = useAuth();
  const today = todayKST();
  const grouped = groupBy !== 'none';
  const summaryOnly = preview && !grouped;
  const result = useReport(async (signal) => {
    const params = { from, to, bucket, groupBy, signal };
    // 미래 휴원·복귀까지 포함된 기말에서 오늘의 재원 수를 역산할 수는 없다.
    const [data, confirmed, students] = await Promise.all([
      getFlows(params, getAccessToken),
      from <= today && to > today ? getFlows({ ...params, to: today }, getAccessToken) : null,
      grouped && from <= today && to >= today ? listAllStudents({ asOf: today, state: 'enrolled', signal }, getAccessToken) : null,
    ]);
    const value = confirmed ? { ...data, buckets: data.buckets.map((period) => ({
      ...period, rateBasis: confirmed.buckets.find((item) => item.start === period.start),
    })) } : data;
    // 이전 결과를 유지할 때도 숫자와 조회 기간은 같은 요청에 속해야 한다.
    return { ...value, summaryPeriod: summaryOnly ? withdrawalPeriodSummary(value.buckets, from, to) : null,
      currentGroups: students ? groupStudentCounts(students.items, groupBy) : null };
  }, `flows-${from}-${to}-${bucket}-${groupBy}-${today}`, { keepPreviousData: summaryOnly });
  const action = summaryAction && React.cloneElement(summaryAction, {
    disabled: result.refreshing,
    ...(result.refreshing ? { children: <span role="status" aria-label="퇴원율 조회 중입니다. 이전 결과를 표시합니다." className="inline-flex min-w-9 justify-center"><Loader2 aria-hidden="true" className="size-3.5 animate-spin" /></span> } : {}),
  });
  return <div aria-busy={result.refreshing} className={summaryOnly ? 'flex h-full flex-col' : undefined}><Result result={result} compact={preview}>{(data) => {
    if (grouped) return <GroupFlowTable data={data} groupBy={groupBy} preview={preview} today={today} />;
    if (!data.buckets.length) return <p className="py-3 text-xs text-gray-500">조회 기간에 통계가 없습니다.</p>;
    const rows = data.buckets.map((period) => ({ ...period.total, period, confirmed: confirmedFlowRow(period.total, 'none', period, today) }));
    return (
      <div className={summaryOnly ? 'flex flex-1 flex-col gap-1.5' : 'space-y-3'}>
        {!preview && data.data_reliable_from && <p className="text-xs text-gray-500">집계 자료 시작일 {data.data_reliable_from}</p>}
        {data.buckets.some((period) => period.total?.estimated_events > 0) && <p className="text-xs text-gray-500">일부 날짜는 이관 자료를 기준으로 합니다.</p>}
        <WithdrawalRateChart buckets={summaryOnly ? [data.summaryPeriod] : data.buckets} compact={summaryOnly} action={summaryOnly ? action : undefined} />
        {!summaryOnly && (
          <ReportTable label="입원·퇴원 통계" headings={['기간', '수강 시작', '그중 복귀', '퇴원·휴원(건)', '그중 휴원(건)', '기간 말 수강', '퇴원율']}>
            {rows.map((row) => (
              <tr key={row.period.start}>
                <th scope="row" className="p-2 text-left font-normal text-gray-900">
                  {row.period.start} ~ {row.period.end}
                  {row.period.partial && <span className="ml-1 text-gray-500">일부 기간</span>}
                </th>
                {[row.confirmed?.active_in, row.confirmed?.resumed, row.confirmed?.stopped, row.confirmed?.stopped_pause, row.confirmed?.active_closing].map((value, i) => <td key={i} className="p-2 text-right tabular-nums text-gray-700">{displayNumber(value)}</td>)}
                <td className="p-2 text-right tabular-nums text-gray-900">{formatRate(flowWithdrawalStats(row, 'none', row.period).rate)}</td>
              </tr>
            ))}
          </ReportTable>
        )}
        {!preview && <>
          <p className="text-xs text-gray-500">수강 시작은 신규·재등록·복귀를 포함합니다. 퇴원은 휴원을 포함한 수강 중단 횟수입니다. 휴원은 시작일 전날, 퇴원은 마지막 수강일이 속한 기간에 집계합니다. 휴원 중 퇴원은 다시 세지 않습니다. 기간 말 수강은 휴원생을 제외하고 해당 날짜가 끝난 뒤에도 계속 다니는 인원입니다. 오늘 이후가 포함된 기간은 오늘까지 확정된 값으로 계산합니다. 테스트 학생은 제외합니다.</p>
          {rows.some((row) => ['scheduled_registered_new', 'scheduled_registered_again', 'scheduled_stopped'].some((key) => row[key] > 0)) && (
            <ReportTable label="예정 인원" headings={['기간', '신규 예정', '재등록 예정', '수강 중 퇴원 예정(건)']}>
              {rows.map((row, index) => <tr key={index}>
                <th scope="row" className="p-2 text-left font-normal">{row.period.start} ~ {row.period.end}</th>
                {[row.scheduled_registered_new, row.scheduled_registered_again, row.scheduled_stopped].map((value, i) => <td key={i} className="p-2 text-right">{displayNumber(value)}</td>)}
              </tr>)}
            </ReportTable>
          )}

        </>}
      </div>
    );
  }}</Result>
    {summaryOnly && !result.data?.buckets.length && <div className="text-right">{action}</div>}
  </div>;
}

export function HeadcountContent({ asOf, by = 'grade', schoolLevel, onSchoolLevelChange }) {
  const { getAccessToken } = useAuth();
  const result = useReport((signal) => getHeadcount({ asOf, groupBy: by, splitBy: by === 'school' ? 'grade' : undefined, signal }, getAccessToken), `headcount-${asOf}-${by}`);
  return <Result result={result}>{(data) => by === 'school'
    ? <SchoolGradeTable groups={data.groups} levelValue={schoolLevel} onLevelChange={onSchoolLevelChange} />
    : <Box title="학년별 재원" sub="휴원 제외">
      <ColumnBars ariaLabel="학년별 재원 인원" items={data.groups.map((g) => ({ label: g.label, value: g.count - g.paused }))} />
      <div className="mt-4"><ReportTable label="학년별 재원 상세" headings={['학년', '재원', '휴원']}>
        {data.groups.map((row) => <tr key={row.key ?? 'none'}><th scope="row" className="p-2 text-left font-normal">{row.label}</th><td className="p-2 text-right">{displayNumber(row.count - row.paused)}</td><td className="p-2 text-right text-gray-500">{displayNumber(row.paused)}</td></tr>)}
      </ReportTable></div>
    </Box>}</Result>;
}

export function WithdrawContent({ from, to, onOpenStudent }) {
  const { getAccessToken } = useAuth();
  const result = useReport((signal) => getWithdrawScheduled({ from, to, signal }, getAccessToken), `withdraw-${from}-${to}`);
  return <Result result={result}>{(data) => data.items.length ? <><p className="text-xs text-gray-500">퇴원일은 마지막 수강일입니다. 해당 날짜까지 재원하며 반·담임도 그날 기준입니다. 휴원 중 퇴원은 앞선 휴원 때 이미 집계하므로 퇴원율에 다시 넣지 않습니다. · 총 {displayNumber(data.total_items)}명</p><ReportTable label="퇴원 예정 학생" headings={['학생', '퇴원일(마지막 수강일)', '반 · 담임', '사유']}>
    {data.items.map((row) => <tr key={`${row.student_id}-${row.end_on}`}>
      <th scope="row" className="p-2 text-left font-normal"><NameLink id={row.student_id} name={row.student_name} onOpen={onOpenStudent} /></th>
      <td className="p-2 text-right">{row.last_day || '—'}</td>
      <td className="p-2 text-right">{(row.classes || []).map((c) => `${c.class_name}${c.teacher?.name ? ` · ${c.teacher.name}` : ''}`).join(', ') || '반 없음'}</td>
      <td className="p-2 text-right">{row.while_paused && <span className="mr-1 rounded-full bg-yellow-50 px-2 py-0.5 text-yellow-800">휴원 중 퇴원</span>}{row.reason || '—'}</td>
    </tr>)}
  </ReportTable></> : <p className="py-4 text-xs text-gray-500">조회 기간에 퇴원 예정 학생이 없습니다.</p>}</Result>;
}

export function NotesPreview({ onOpenNotes }) {
  const { getAccessToken } = useAuth();
  const result = useReport((signal) => getNotesPreview({ signal }, getAccessToken), 'academy-class-note-preview');
  return <Result result={result} compact>{(data) => data.items.length ? <FloatingScrollArea axis="y" aria-label="최근 학원·반 메모" className="max-h-28"><ul className="divide-y divide-gray-100">
    {data.items.map((note) => {
      const Tag = onOpenNotes ? 'button' : 'div';
      return <li key={note.id} className="py-1 text-xs"><Tag {...(onOpenNotes ? { type: 'button', onClick: () => onOpenNotes(note.class_id) } : {})} className={`block w-full min-w-0 text-left ${onOpenNotes ? 'rounded hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary' : ''}`}>
        <p className="line-clamp-2 break-words text-gray-900">{note.is_pinned && <span className="mr-1 text-primary-dark">고정</span>}{note.body}</p>
        <p className="break-words text-gray-500">{note.class_id == null ? '학원' : note.class_name || '반'} · {note.occurred_on} · {note.author_name}</p>
      </Tag></li>;
    })}
  </ul></FloatingScrollArea> : <p className="py-1 text-xs text-gray-500">학원·반 메모가 없습니다.</p>}</Result>;
}
